import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useQuery } from '@tanstack/react-query';

import { api } from '@/api/client';
import { Spinner } from '@/components/ui/Spinner';
import { formatDateTime, type DateFormat } from '@/lib/dates';

import { CANDIDATES_KEY } from '../api';
import styles from './HistoryTooltip.module.css';

/** One recorded change, as the API sends it. */
type StatusEvent = {
  id: string;
  field: string;
  from_value: string | null;
  to_value: string;
  source: string;
  note: string | null;
  occurred_at: string;
  changed_by: { id: string; full_name: string; email: string } | null;
};

/** The five fields the API keeps a history of. Mirrors `TrackedField` on the server. */
export type TrackedField =
  'status' | 'flyst_stage' | 'rsr_state' | 'documentation' | 'payment_status';

/**
 * What a chip needs to know to show its own history.
 *
 * One object rather than two props, so a call site cannot pass the candidate and forget
 * the date format, and so adding a third thing later does not touch five components.
 */
export type HistoryAnchor = { candidateId: string; dateFormat: DateFormat };

const GAP = 6;
const EDGE = 12;
const CARD_WIDTH = 300;
/** Enough for the head and about four entries. Below this it opens upwards instead. */
const CARD_HEIGHT = 220;

type Placement = { top: number; left: number; below: boolean };

export function HistoryTooltip({
  candidateId,
  dateFormat,
  field,
  label,
  describe,
  children,
}: HistoryAnchor & {
  field: TrackedField;
  /** What this is the history of. Read out before the entries. */
  label: string;
  /** Turns a stored value into the words the rest of the screen uses for it. */
  describe: (value: string) => string;
  children: ReactNode;
}) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);

  // Hover and focus are tracked apart rather than as one `open` flag. Sharing one, a
  // mouse that passes over a chip the keyboard is sitting on closes the card out from
  // under it on the way out.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);

  const open = (hovered || focused) && !dismissed;

  /**
   * Fetched only once opened.
   *
   * A page of rows carries several of these each. Loading them with the list would be
   * hundreds of requests to answer a question nobody has asked.
   */
  const history = useQuery({
    queryKey: [...CANDIDATES_KEY, 'history', candidateId, field],
    queryFn: () =>
      api.get<StatusEvent[]>(`/api/v1/candidates/${candidateId}/history`, { query: { field } }),
    enabled: open,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Flip up when there is not room below, unless there is even less room above.
      const roomBelow = window.innerHeight - rect.bottom;
      const below = roomBelow >= CARD_HEIGHT || roomBelow >= rect.top;

      // Held inside the window, so a chip in the last column of a wide table does not
      // put half its history past the right-hand edge.
      const left = Math.min(
        Math.max(EDGE, rect.left),
        Math.max(EDGE, window.innerWidth - CARD_WIDTH - EDGE),
      );

      setPlacement({
        top: below ? rect.bottom + GAP : rect.top - GAP,
        left,
        below,
      });
    };

    place();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDismissed(true);
    };

    document.addEventListener('keydown', onKeyDown);
    // Capture phase: the scroll that moves this is usually the table's own, and a
    // scroll event on an inner element does not bubble to the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const events = history.data ?? [];

  const card = (
    <div
      id={tooltipId}
      role="tooltip"
      className={styles.card}
      style={{
        insetBlockStart: placement?.top,
        insetInlineStart: placement?.left,
        // Anchored by its bottom edge when it opens upwards, so it grows away from the
        // chip instead of over it.
        transform: placement?.below ? undefined : 'translateY(-100%)',
      }}
    >
      <p className={styles.head}>{label}</p>

      {history.isPending ? (
        <p className={styles.empty}>
          <Spinner size={14} label="Loading the history" />
        </p>
      ) : history.isError ? (
        <p className={styles.empty}>That history did not load.</p>
      ) : events.length === 0 ? (
        <p className={styles.empty}>Nothing recorded yet.</p>
      ) : (
        <ol className={styles.list}>
          {events.map((event) => (
            <li key={event.id} className={styles.event}>
              <span className={styles.change}>
                {event.from_value === null ? (
                  <>
                    Started at <b>{describe(event.to_value)}</b>
                  </>
                ) : (
                  <>
                    {describe(event.from_value)} <span aria-hidden="true">→</span>{' '}
                    <b>{describe(event.to_value)}</b>
                  </>
                )}
              </span>
              <span className={styles.meta}>
                {formatDateTime(event.occurred_at, dateFormat)}
                {/* "Nobody did this" and "we do not know who did" are different
                    answers, and the backfill is the first one. */}
                {event.changed_by
                  ? ` · ${event.changed_by.full_name}`
                  : event.source === 'import'
                    ? ' · from the import'
                    : ''}
              </span>
              {event.note ? <span className={styles.note}>{event.note}</span> : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );

  return (
    <>
      <span
        ref={triggerRef}
        className={styles.trigger}
        // Focusable, because the history has to be reachable without a mouse.
        // `aria-describedby` and not a label: the chip already says what the value is,
        // and this is the detail behind it.
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
          // Escape's effect lasts until the chip is left entirely. Clearing it any
          // sooner and the card springs back open under a stationary cursor; never
          // clearing it and a chip dismissed once stays silent for good.
          if (!focused) setDismissed(false);
        }}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
          if (!hovered) setDismissed(false);
        }}
      >
        {children}
      </span>

      {/*
        Into the body, not in place. `position: fixed` escapes the `overflow` on the
        table and on a drawer, but not a transformed ancestor, and the drawer's entry
        keyframe animates `transform`, which for those few hundred milliseconds makes
        the drawer the containing block and drags the card back inside it. A portal has
        no ancestors to be caught by.
      */}
      {open && placement ? createPortal(card, document.body) : null}
    </>
  );
}
